import React from "react"
import styles from "./charts.module.css"
import InfoTooltip from "../../../components/InfoTooltip"
import ChartPie from "./Pie"
import ChartBar from "./Bar"
import ChartLine from './Line/index';
import ChartDonut from "./Donut"
import ChartGauge from "./Gauge"
import ChartHeatmap from './Heatmap/index';
import ChartArea from "./Area"
import { formatCompactNumber, formatValueWithUnit, inferChartUnit } from "./chartUtils"

const chartTypeLabels = {
    pie: "Distribuicao",
    bar: "Ranking",
    line: "Tendencia",
    donut: "Composicao",
    gauge: "Indicador",
    heatmap: "Concentracao",
    area: "Volume"
}

const chartDescriptions = {
    pie: "Distribuicao percentual dos itens no periodo filtrado.",
    bar: "Comparativo entre categorias, ordenado pelo volume ou valor retornado no filtro.",
    line: "Evolucao dos resultados ao longo do periodo selecionado.",
    donut: "Participacao de cada grupo em relacao ao total.",
    gauge: "Indicador percentual de desempenho em relacao a meta esperada.",
    heatmap: "Concentracao de ocorrencias por dia e horario.",
    area: "Volume acumulado ou tendencia dos resultados ao longo do periodo."
}

const getChartDescription = (data) => {
    return data.tooltip || data.description || chartDescriptions[data.type]
}

const getNumericValue = (item, valueKey = "value") => {
    const value = item?.[valueKey]
    return Number.isFinite(Number(value)) ? Number(value) : 0
}

const getChartSummary = (data) => {
    if (data.type === "gauge") {
        return data.value !== undefined ? `${formatCompactNumber(data.value)}%` : ""
    }

    const values = data.values || []
    const valueKey = data.valueKey || "value"
    const unit = inferChartUnit(data)
    const total = values.reduce((acc, item) => acc + getNumericValue(item, valueKey), 0)

    if (!values.length) return "Sem registros"
    if (data.type === "heatmap") return formatValueWithUnit(total, unit)
    if (data.type === "line" || data.type === "area") {
        const peak = values.reduce((max, item) => Math.max(max, getNumericValue(item, valueKey)), 0)
        return `Pico ${formatValueWithUnit(peak, unit)}`
    }

    return `Total ${formatValueWithUnit(total, unit)}`
}

const getChartMeta = (data) => {
    const count = data.values?.length || 0
    if (data.type === "gauge") return "Percentual"
    if (data.type === "heatmap") return "Por dia e hora"
    if (data.type === "line" || data.type === "area") return `${count} pontos`
    return `${count} categorias`
}

const Chart = ({ data }) => {
    const description = getChartDescription(data)
    const summary = data.summary || getChartSummary(data)
    const meta = data.metaLabel || getChartMeta(data)
    const typeLabel = chartTypeLabels[data.type] || "Analise"
    const unit = inferChartUnit(data)

    return (
        <div
            className={`${data.type === "pie" ? styles.chartPie : (data.type === "bar" ? styles.chartBar : styles.chartItem)}`}
            data-dashboard-chart-id={data.id}
            key={data.id}
        >
            <div className={styles.chartHeader}>
                <div className={styles.chartTitleGroup}>
                    <span className={styles.chartEyebrow}>{typeLabel}</span>
                    <div className={styles.chartTitleRow}>
                        <span className={styles.chartTitle}>{data.title}</span>
                        {description && (
                            <InfoTooltip
                                text={description}
                                preferredPosition="top"
                            />
                        )}
                    </div>
                </div>
                <div className={styles.chartHeaderMetric}>
                    <strong>{summary}</strong>
                    <span>{meta}</span>
                </div>
            </div>
            <div className={styles.chartBody}>
                {data.type === "pie" &&
                    <ChartPie
                        customColors={data.colors}
                        data={data.values}
                        valueKey={data.valueKey}
                        unit={unit}
                    />
                }
                {data.type === "bar" &&
                    <ChartBar
                        color={data.color}
                        data={data.values}
                        unit={unit}
                    />
                }
                {data.type === "line" &&
                    <ChartLine
                        data={data.values}
                        color={data.color}
                        unit={unit}
                    />
                }
                {data.type === "area" &&
                    <ChartArea
                        data={data.values}
                        color={data.color}
                        unit={unit}
                    />
                }
                {data.type === "donut" &&
                    <ChartDonut
                        data={data.values}
                        colors={data.colors}
                        unit={unit}
                    />
                }
                {data.type === "gauge" &&
                    <ChartGauge
                        value={data.value}
                        color={data.color}
                    />
                }
                {data.type === "heatmap" &&
                    <ChartHeatmap
                        data={data.values}
                        unit={unit}
                    />
                }
            </div>
        </div>
    )
}

export default Chart
